import { Language } from "@/components/language/supported-languages";
import axios from "axios";
import { useState } from "react";

// Define types
export interface TranslationRequest {
  source_language: string;
  target_language: string;
  target_language_code: string;
  text: string;
  options: number;
  with_audio: boolean;
}

export interface TranslationResponse {
  text: string;
  base64audio?: string; // present in rest api response
  src?: string; // present in websocket response
}

export function useTextTranslation() {
  const [isTranslationLoading, setIsLoading] = useState<boolean>(false);
  const [translationError, setError] = useState<string>("");

  const translateText = async (
    session: string,
    token: string,
    inputText: string,
    sourceLanguage: Language,
    targetLanguage: Language,
    withAudio: boolean,
    options: number = 3
  ) => {
    setIsLoading(true);
    setError("");

    try {
      const url =
        import.meta.env.VITE_REACT_APP_VOICECHAT_URL + "/v0/translate";
      const urlWithParams = new URL(url);
      urlWithParams.searchParams.append("session", session);

      const headers = {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      };

      const requestPayload: TranslationRequest = {
        source_language: sourceLanguage.name,
        target_language: targetLanguage.name,
        target_language_code: targetLanguage.key,
        text: inputText,
        options: options,
        with_audio: withAudio,
      };

      console.log("Translation request payload:", requestPayload);

      const response = await axios.post(
        urlWithParams.toString(),
        requestPayload,
        {
          headers: headers,
        }
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      const translations = response.data?.translations as TranslationResponse[];

      return translations;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const errorMessage = `${error.status} ${error.response?.data}`;
        setError(errorMessage);
        throw new Error(errorMessage);
      } else {
        console.error(error);
        setError("An unknown error occurred");
        throw error;
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    translateText,
    isTranslationLoading,
    translationError,
  };
}
